import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import Fab from '@mui/material/Fab'
import TableCell, { TableCellProps } from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { TableLoader } from '@components/skeletonLoader/tableLoader'
import { CvsTable } from '@components/table/cvs-table'
import { colors } from '@global/constants/colors'
import { api } from 'api'
import * as css from './styles'
import { t } from '@lingui/core/macro'

// TODO: this type needs to be anchored somewhere, this is a temporary solution
interface Submission {
  datasetId: string
  templateName: string
  submissionName: string
  rowCount: number
  createdAt: string
  status: string
  [key: string]: string | number
}

async function fetchRecentDatasets() {
  const { data } = await api.get<Submission[]>(
    '/api/core/v1/reports/home/recent-submissions?limit=6',
  )
  return data
}

export const RecentSubmissions = (props: { keyField: string }) => {
  const history = useHistory()
  const { data: submissions, isInitialLoading: isLoading } = useQuery({
    queryKey: ['recentSubmissions'],
    queryFn: () => fetchRecentDatasets(),
    refetchOnWindowFocus: false,
  })
  const columns = [
    { label: t`Template`, width: '20%%', lookup: 'templateName' },
    { label: t`Name`, width: '20%%', lookup: 'submissionName' },
    { label: t`Number of Codes`, width: '15%%', lookup: 'rows' },
    { label: t`Date`, width: '20%', lookup: 'createdAt' },
    { label: t`Status`, width: '15%', lookup: 'status' },
  ]

  function handleRowClick(row: { datasetId: string }) {
    const { datasetId } = row
    history.push(`/codes/details/${datasetId}/data`)
  }

  return (
    <>
      <Typography
        variant="h4"
        gutterBottom
        style={{ padding: 24, margin: 0, display: 'flex', alignItems: 'center' }}
      >
        <FileCopyOutlinedIcon style={{ marginRight: 10 }} /> {t`Recent submissions`}
      </Typography>
      {!isLoading ? (
        <CvsTable
          keyField="id"
          orderBy="last_created"
          columns={columns}
          rows={(submissions ?? []) as never[]} // TODO: fix this type
          noAdd={true}
          noPagination={true}
          overrideRootStyles={css.overrideRootStyles}
          overrideTableWrapperStyles={css.overrideTableWrapperStyles}
          emptyStateMessage={{
            icon: (
              <Fab
                style={{
                  height: '65px',
                  width: '65px',
                  color: colors.gray.light,
                  cursor: 'auto',
                }}
                disabled
              >
                <FileCopyOutlinedIcon style={{ fontSize: '34px', color: colors.blue.smokey }} />
              </Fab>
            ),
            title: t`No Recent Submissions`,
            description: t`As you use Claravine, your recently used submissions will appear here for quick access.`,
          }}
        >
          {({ row, index }: { row: Submission; index: number }) => {
            const altRow = index % 2 === 0
            const cellProps: TableCellProps = {
              component: 'th',
              scope: 'row',
              padding: 'normal',
            }
            const { templateName, submissionName, rowCount } = row
            return (
              <TableRow
                hover
                className={altRow ? '-is-alt-row' : ''}
                onClick={_event => {
                  handleRowClick(row)
                }}
                tabIndex={-1}
                key={row[props.keyField]}
                css={css.tableRowStyle}
              >
                <TableCell {...cellProps} css={css.cellPropStyle}>
                  {templateName}
                </TableCell>
                <TableCell {...cellProps} css={css.cellPropStyle}>
                  {submissionName}
                </TableCell>
                <TableCell {...cellProps} css={css.cellPropStyle}>
                  {rowCount}
                </TableCell>
                <TableCell {...cellProps} css={css.cellPropStyle}>
                  {moment(row.createdAt).format('MM/DD/YY')}
                </TableCell>
                <TableCell {...cellProps} css={css.cellPropStyle}>
                  {row.status}
                </TableCell>
              </TableRow>
            )
          }}
        </CvsTable>
      ) : (
        <TableLoader
          columns={columns}
          overrideContainerStyle={css.overrideRootStyles}
          overrideTableStyle={css.overrideTableLoader}
        />
      )}
    </>
  )
}
